import {Component, Input, Optional} from '@angular/core';
import {ButtonComponent} from "../../ui/button/button.component";
import {ModalBodyComponent} from "../../ui/modal-body/modal-body.component";
import {ModalComponent} from "../../ui/modal/modal.component";
import {ModalFooterComponent} from "../../ui/modal-footer/modal-footer.component";
import {ModalTitleComponent} from "../../ui/modal-title/modal-title.component";
import {TranslateModule} from "@ngx-translate/core";
import {OverlayRef} from "@angular/cdk/overlay";
import {ModalConfig} from "../../../models/modal-config";
import {Project} from "../../../models/project";
import {ProjectStatus} from "../../../enum/project-status";
import {SharedModule} from "../../../shared/shared.module";
import {FormGroupComponent} from "../../ui/form-group/form-group.component";
import {InputComponent} from "../../ui/input/input.component";
import {SelectComponent} from "../../ui/select/select.component";
import {TextareaComponent} from "../../ui/textarea/textarea.component";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ProjectService} from "../../../services/project.service";
import {firstValueFrom, map, of, switchMap, tap} from "rxjs";
import {AsyncPipe, NgIf} from "@angular/common";
import {Router} from "@angular/router";

@Component({
  selector: 'app-edit-project-modal',
  standalone: true,
  imports: [
    ButtonComponent,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    ModalTitleComponent,
    SharedModule,
    TranslateModule,
    FormGroupComponent,
    InputComponent,
    SelectComponent,
    TextareaComponent,
    NgIf,
    ReactiveFormsModule,
    AsyncPipe
  ],
  templateUrl: './edit-project-modal.component.html',
  styleUrl: './edit-project-modal.component.scss'
})
export class EditProjectModalComponent {
  public project?: Project;
  public form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    customer: new FormControl('', [Validators.required]),
    externalId: new FormControl(''),
    status: new FormControl(ProjectStatus.ACTIVE, [Validators.required]),
  });
  public loading = false;
  public loadingExternalIdCheck = false;
  public error = false;
  public statusOptions = [
    { value: ProjectStatus.ACTIVE, labelKey: 'project.status.active' },
    { value: ProjectStatus.ARCHIVED, labelKey: 'project.status.archived' },
  ];
  public externalIdExists$ = this.form.get('externalId')!.valueChanges.pipe(
    tap(() => this.loadingExternalIdCheck = true),
    switchMap(externalId => !externalId || externalId === '' || this.form.get('externalId')!.value === this.project?.externalId ? of(false) : this.projectService.externalIdExists(externalId)),
    tap(() => this.loadingExternalIdCheck = false),
  );

  constructor(private overlayRef: OverlayRef, private modalConfig: ModalConfig, private projectService: ProjectService, private router: Router) {
    this.project = this.modalConfig?.data?.project;
    if (this.project) {
      this.form.patchValue(this.project);
    }
  }

  async save() {
    if (this.loading || !this.form.valid || this.loadingExternalIdCheck) return;

    this.loading = true;
    this.error = false;

    try {
      const body = { ...this.project, ...this.form.value } as Project;
      let result;

      if (this.isEditMode) {
        result = await firstValueFrom(this.projectService.update(body));
        Object.assign(this.project!, result);
      } else {
        result = await firstValueFrom(this.projectService.create(body));
        this.router.navigate(['panel', 'projects', 'detail', result.id]);
      }

      this.close();
    } catch (e) {
      console.error(e);
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  close() {
    this.overlayRef.dispose();
  }

  get isEditMode() {
    return !!this.project;
  }
}
